/**
 * `M-10` y `M-11` · El contrato de lo que el comensal mira y busca.
 *
 * Archivo NUEVO, y por la misma razón por la que `subida.ts` lo fue: `menu.ts`
 * está congelado —«quien crea que una forma de aquí está mal, para y lo dice»— y
 * esto es una superficie nueva, no un cambio de forma sobre la que otros ya
 * están construyendo. Lo único que se toma de allí es `esquemaId`, que se
 * importa en vez de copiarse: una sola definición de cada forma.
 *
 * ── Qué mide cada una, con su limitación escrita ────────────────────────────
 * `vault/10-Contexto/Metricas.md`:
 *
 *   M-10 · «Platillos más vistos y más buscados. **Limitación honesta: mide
 *          interés, no venta** — sin integración de punto de venta no sabemos
 *          qué se pidió.»
 *   M-11 · «Búsquedas sin resultado. Demanda insatisfecha, literal.»
 *
 * Y M-11 no es un tipo de evento aparte: **es una búsqueda con `resultados` en
 * cero**. Ver la cabecera de la migración 041.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  LA RESTRICCIÓN DE PRIVACIDAD, QUE DECIDE LA FORMA ENTERA DE ESTE ARCHIVO
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `Metricas.md`, textual: «Se registran **agregados y anónimos**, sin
 * `customer_id`. Saber qué platillo mira cada persona identificada no aporta al
 * piloto y sí agrega responsabilidad legal.» Y `ADR-012`: «`fialrest-test` no
 * recibe nunca el dato de una persona real. Ni un teléfono, ni un nombre […]
 * **No hay versión "por hoy nada más"**.»
 *
 * De ahí que en estos tipos **no haya dónde poner una persona**:
 *
 *  · Ni `customerId`, ni `sesionId`, ni `dispositivoId`, ni nada parecido. No es
 *    que se manden vacíos: es que no existen, así que no se pueden rellenar por
 *    descuido dentro de un año.
 *  · `.strict()` en los dos esquemas — un campo de más **se rechaza**, no se
 *    ignora. Es la diferencia entre «no lo guardamos» y «no lo aceptamos», y con
 *    un dato personal esa diferencia es toda la garantía: un cliente que empiece
 *    a mandar `{ ..., telefono }` recibe un 400 en vez de que el campo viaje
 *    hasta el servidor y muera ahí, habiendo pasado por los registros de en
 *    medio.
 *  · **Ni IP ni user-agent**, que no se declaran aquí porque no hacen falta:
 *    llegan solos en toda petición HTTP, y lo que importa es que la API **no los
 *    escriba** — la tabla de la 041 no tiene columna para ellos.
 *
 * Lo único que viene de una persona es el texto que escribió. Ver
 * `esquemaTextoBuscado`, que es donde vive la parte incómoda de esto.
 */
import { z } from 'zod';
import { esquemaId } from './menu.js';
/**
 * 60 caracteres, y el mismo número que el `check` de la migración 041.
 *
 * `Metricas.md` pide que el texto se guarde y esta tarea pide que vaya
 * **acotado**; el número sale de para qué sirve el campo: se busca sobre nombres
 * de platillo, y el nombre más largo que esta base admite son los 80 de
 * `tenants.name` — un platillo no llega ni a eso. Sesenta deja sitio de sobra
 * para «capuchino descafeinado sin lactosa» y corta cualquier intento de usar
 * esta columna como buzón.
 *
 * Vive aquí y no sólo en la migración para que el navegador pueda cortar
 * **antes** de mandar, con el mismo número: una sola definición.
 */
export const LARGO_MAXIMO_DEL_TEXTO_BUSCADO = 60;
/**
 * Tope de la cuenta de resultados, igual que el `check` de la 041.
 *
 * Ninguna carta de una cafetería tiene mil platillos, así que un número mayor no
 * es un error de conteo: es alguien probando qué admite el campo.
 */
export const MAXIMO_DE_RESULTADOS = 1000;
/**
 * ¿Ese texto parece un dato de una persona?
 *
 * > [!danger] Ésta es la parte incómoda de M-11, y conviene decirla entera
 * > El buscador es una caja de texto libre en la mesa de una cafetería. Nada
 * > impide que alguien teclee ahí su número, o el correo de su novia, o vaya a
 * > saber qué. Y entonces `ADR-012` —«ni un teléfono, ni un nombre»— se rompe
 * > **sin que nadie haya escrito una línea de código mala**: por lo que escribió
 * > un desconocido en un campo que existe para otra cosa.
 * >
 * > El motor no puede impedirlo: sólo acota el largo. Así que se filtra aquí,
 * > en el único sitio donde lo saben los dos lados — el navegador lo aplica
 * > antes de mandar y la API antes de escribir.
 *
 * Dos reglas, y las dos elegidas por ser **imposibles en una búsqueda honesta**
 * de un menú, no por cazar todos los casos:
 *
 *  1. **Siete dígitos seguidos.** Un número local mexicano son siete o diez. En
 *     una carta, el número más largo que alguien busca es un precio o un gramaje
 *     —«500 ml», «250 g»—, que son tres. Se cuenta sobre los dígitos con los
 *     separadores quitados, porque «662-123-4567» es el mismo teléfono.
 *  2. **Una arroba.** No hay un solo platillo con `@` en el nombre, y sí hay
 *     correos que la llevan. Es la regla más barata del archivo.
 *
 * > [!warning] Lo que esto NO caza, y hay que saberlo
 * > Un nombre propio. «Busqué *Fernanda*» pasa, porque es indistinguible de
 * > buscar un platillo que se llame así —que los hay— y una lista de nombres
 * > sería un filtro que rechaza «Margarita» en una cafetería que vende una.
 * >
 * > No se finge lo contrario: **esto reduce el riesgo, no lo cierra**. Lo que sí
 * > lo cierra es lo de al lado: la fila no tiene con qué atar ese texto a nadie
 * > —ni sesión, ni IP, ni dispositivo—, así que un nombre suelto en una columna
 * > de 60 caracteres, sin nada más, no identifica a nadie. Es exactamente lo que
 * > `Metricas.md` quiere decir con «agregados y anónimos».
 */
export const pareceDatoPersonal = (texto) => texto.includes('@') || /\d{7,}/.test(texto.replace(/[\s.\-()+]/g, ''));
/**
 * El texto buscado, tal como puede viajar.
 *
 * `.trim()` antes de medir: un texto de espacios no es una búsqueda, es que
 * alguien tocó el campo. El `.min(1)` **después** del `trim` es deliberado y la
 * lección es de `menu.ts`: sin él, `'   '` pasa el contrato, llega al motor y el
 * `check (length(trim(search_text)) > 0)` lo rechaza — o sea **un 500 donde
 * debía haber un 400**.
 *
 * El `refine` de datos personales va al final para que un texto largo *y* con
 * pinta de teléfono se queje primero de lo que se puede arreglar.
 */
export const esquemaTextoBuscado = z
    .string()
    .trim()
    .min(1)
    .max(LARGO_MAXIMO_DEL_TEXTO_BUSCADO)
    .refine((t) => !pareceDatoPersonal(t), {
    message: 'Ese texto parece un dato personal, y las métricas del menú se registran ' +
        'agregadas y anónimas (Metricas.md, ADR-012). El evento se descarta.',
});
/**
 * Cuántos platillos encontró la búsqueda. **Cero es M-11.**
 *
 * > [!warning] Lo declara el navegador y no se puede verificar
 * > La búsqueda de `T-36` corre en el teléfono, sobre la carta que ya está en
 * > memoria, y `apps/menu/src/buscar.ts` explica por qué eso es lo correcto. El
 * > servidor no la repite: reimplementarla aquí —con el plegado de acentos y
 * > todo— sería una segunda fuente de verdad sobre qué encuentra una búsqueda.
 * >
 * > Así que M-10 y M-11 miden **lo que el navegador dice**, igual que M-03 mide
 * > escaneos y no reseñas. Está dicho en voz alta en vez de fingir lo contrario.
 */
export const esquemaResultados = z.number().int().min(0).max(MAXIMO_DE_RESULTADOS);
/**
 * Lo que el navegador manda para anotar UN evento.
 *
 * Unión discriminada y no un objeto con campos opcionales, por la misma razón
 * que `CafeteriaPublica`: una vista **no tiene texto** y una búsqueda **no tiene
 * platillo**, y el tipo lo hace imposible de rellenar por descuido. Con campos
 * opcionales cabría un `{ platilloId, texto }` que no significa nada y que el
 * motor rechazaría con el CHECK `menu_events_forma` — un 500 donde debía haber
 * un 400, otra vez.
 *
 * Los dos discriminantes van en español como el resto de los identificadores del
 * proyecto; el motor los guarda en inglés (`item_view`, `search`) como el resto
 * de sus conjuntos cerrados, y la traducción vive en **un solo sitio**: el
 * repositorio de `apps/api/src/publico/eventos-del-menu.ts`.
 */
export const esquemaEventoDelMenu = z.discriminatedUnion('tipo', [
    z
        .object({
        /** M-10 · Se abrió la ficha de un platillo. */
        tipo: z.literal('platillo-visto'),
        /**
         * Cuál. Se valida como UUID aquí y no en el servicio porque un
         * `where id = 'hola'` sobre una columna `uuid` **no devuelve cero filas:
         * revienta**, y ese error sale por el endpoint como un 500. Es la misma
         * razón por la que `esquemaId` existe en `menu.ts`.
         */
        platilloId: esquemaId,
    })
        .strict(),
    z
        .object({
        /** M-10 («más buscados») y, con `resultados` en cero, M-11. */
        tipo: z.literal('busqueda'),
        texto: esquemaTextoBuscado,
        resultados: esquemaResultados,
    })
        .strict(),
]);
//# sourceMappingURL=eventos-del-menu.js.map